import type { DatabaseClipboardInsert } from '@blocksuite/affine/shared/adapters';
import {
  livePageHosting,
  publishDatabaseCreationDocuments,
} from '@blocksuite/affine-block-database';
import type { HistoryStepHold, Store } from '@blocksuite/store';
import {
  cloneDatabaseColumns,
  createDatabaseReferenceProps,
  createDatabaseShardManifest,
  createDatabaseShardManifestEntry,
  createDatabaseShardRowWriteFields,
  createServerDatabaseRootDocId,
  DATABASE_DIRECTORY_PART_MAP_KEY,
  DATABASE_DIRECTORY_PART_SHARDS_ARRAY_KEY,
  DATABASE_ROW_SHARD_HARD_LIMIT_BYTES,
  DATABASE_ROW_SHARD_ORDER_ARRAY_KEY,
  DATABASE_ROW_SHARD_ROWS_MAP_KEY,
  databaseShardDirectoryPartMap,
  databaseShardDocWriteClocks,
  type DatabaseShardManifestEntry,
  deleteDatabaseShardDirectory,
  DOCUMENT_PRODUCT_HARD_LIMIT_BYTES,
  PAGE_DOCUMENT_PART_ID,
  pageDocumentStructure,
  PageStructureWriter,
  readDatabaseShardDirectoryBlock,
  readDatabaseShardDirectoryBlockPartDocIds,
  writeDatabaseShardDirectory,
} from '@streamlain/database-identity';
import { PublicationError } from '@streamlain/doc-publication-contract';
import * as Y from 'yjs';

/** Copies a database in the client, local and cloud alike: the detached root,
 * parts and shards are prepared here; the returned insertion places the host
 * after the pasted blocks and publishes it all with that page as one
 * creation, so no partially copied database becomes visible. */
export async function prepareDatabaseCopy(
  input: DatabaseClipboardInsert,
  load: (docId: string) => Promise<Store>
): Promise<(hold: HistoryStepHold) => Promise<void>> {
  const { source, snapshot, draft, doc, parentId, index } = input;
  const rootId = createServerDatabaseRootDocId({
    workspaceId: doc.workspace.id,
    databaseId: source.databaseId,
  });
  const root = await load(rootId);
  const original = root.spaceDoc.getMap('blocks').get(source.databaseId);
  if (
    !(original instanceof Y.Map) ||
    original.get('sys:flavour') !== 'affine:database'
  ) {
    throw new Error('Database source is unavailable');
  }
  if (
    original.get('prop:systemManaged') ||
    original.get('prop:managedBy') ||
    original.get('prop:docScoped')
  ) {
    throw new Error('System-managed databases cannot be copied');
  }
  // The shard entries live in the part documents of the directory.
  const sourceParts = new Map<string, Store>();
  for (const partDocId of readDatabaseShardDirectoryBlockPartDocIds(original))
    sourceParts.set(partDocId, await load(partDocId));
  const manifest = readDatabaseShardDirectoryBlock(original, partDocId => {
    const part = sourceParts.get(partDocId);
    return part && databaseShardDirectoryPartMap(part.spaceDoc);
  });
  if (
    !manifest ||
    manifest.databaseId !== source.databaseId ||
    manifest.workspaceId !== doc.workspace.id ||
    manifest.databaseRootDocId !== rootId
  ) {
    throw new Error('Database copy requires a canonical source manifest');
  }
  if (
    manifest.shards.length > 62 ||
    manifest.shards.some(shard => shard.status !== 'ready')
  ) {
    throw new Error(
      'Database copy exceeds its shard budget or has unfinished shards'
    );
  }
  const rootVersion = Y.encodeStateVector(root.spaceDoc);
  const partVersions = [...sourceParts.values()].map(part =>
    Y.encodeStateVector(part.spaceDoc)
  );
  const loaded: Array<{
    entry: (typeof manifest.shards)[number];
    store: Store;
  }> = [];
  for (const entry of manifest.shards) {
    // A fresh database can advertise its empty first shard before it exists.
    if (
      entry.rowCount === 0 &&
      entry.encodedBytes === 0 &&
      !doc.workspace.getDoc(entry.serverDocId)
    )
      continue;
    loaded.push({ entry, store: await load(entry.serverDocId) });
  }
  if (!equal(rootVersion, Y.encodeStateVector(root.spaceDoc))) {
    throw new Error('Database source changed during copy');
  }
  const versions = loaded.map(({ store }) =>
    Y.encodeStateVector(store.spaceDoc)
  );
  const prepared: Array<{ docId: string; update: Uint8Array }> = [];
  const entries: DatabaseShardManifestEntry[] = [];
  let bytes = 0;
  for (const entry of manifest.shards) {
    const from = loaded.find(
      item => item.entry.shardId === entry.shardId
    )?.store;
    const rows = from?.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
    const order =
      from?.spaceDoc
        .getArray<string>(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY)
        .toArray() ?? [];
    if (
      (rows?.size ?? 0) !== order.length ||
      order.length !== entry.rowCount ||
      new Set(order).size !== order.length ||
      order.some(id => !rows?.has(id))
    ) {
      throw new Error('Database copy refuses an incomplete row shard');
    }
    const target = new Y.Doc();
    try {
      const copied = target.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
      for (const id of order) {
        const value = rows!.get(id);
        copied.set(
          id,
          value instanceof Y.AbstractType
            ? value.clone()
            : structuredClone(value)
        );
      }
      target.getArray(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY).push(order);
      const update = Y.encodeStateAsUpdate(target);
      bytes += update.length;
      if (
        update.length > DATABASE_ROW_SHARD_HARD_LIMIT_BYTES ||
        bytes > 48 * 1024 * 1024
      ) {
        throw new Error('Database copy exceeds its byte budget');
      }
      const copiedEntry = createDatabaseShardManifestEntry({
        workspaceId: doc.workspace.id,
        databaseId: snapshot.id,
        shardId: entry.shardId,
        ...createDatabaseShardRowWriteFields(
          order,
          databaseShardDocWriteClocks(target)
        ),
        rowCount: order.length,
        firstRowId: order[0],
        lastRowId: order.at(-1),
        encodedBytes: update.length,
        status: 'ready',
      });
      entries.push(copiedEntry);
      prepared.push({ docId: copiedEntry.serverDocId, update });
    } finally {
      target.destroy();
    }
    // Yield between bounded shards; do not monopolize the UI on a large copy.
    await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
  }
  if (
    !equal(rootVersion, Y.encodeStateVector(root.spaceDoc)) ||
    [...sourceParts.values()].some(
      (part, i) => !equal(partVersions[i], Y.encodeStateVector(part.spaceDoc))
    ) ||
    loaded.some(
      ({ store }, i) => !equal(versions[i], Y.encodeStateVector(store.spaceDoc))
    )
  ) {
    throw new Error('Database source changed during copy');
  }
  const reference = createDatabaseReferenceProps({
    workspaceId: doc.workspace.id,
    databaseId: snapshot.id,
  });
  const targetRoot = new Y.Doc();
  const targetParts = new Map<string, Y.Doc>();
  try {
    const block = original.clone();
    const blocks = targetRoot.getMap('blocks');
    const page = new Y.Map<unknown>();
    const pageId = `clone-page:${snapshot.id}`;
    blocks.set(pageId, page);
    page.set('sys:id', pageId);
    page.set('sys:flavour', 'affine:page');
    page.set('sys:version', 2);
    page.set('prop:title', new Y.Text());
    const writer = new PageStructureWriter(pageDocumentStructure(targetRoot));
    writer.placeBlock({
      id: pageId,
      parentId: null,
      partId: PAGE_DOCUMENT_PART_ID,
    });
    blocks.set(snapshot.id, block);
    block.set('sys:id', snapshot.id);
    writer.placeBlock({
      id: snapshot.id,
      parentId: pageId,
      partId: PAGE_DOCUMENT_PART_ID,
    });
    block.set('prop:databaseId', snapshot.id);
    block.set('prop:databaseRootDocId', reference.databaseRootDocId);
    // The copy starts its own directory: a fresh head and part documents.
    deleteDatabaseShardDirectory(block);
    writeDatabaseShardDirectory(
      block,
      partDocId => {
        let part = targetParts.get(partDocId);
        if (!part) {
          part = new Y.Doc({ guid: partDocId });
          targetParts.set(partDocId, part);
        }
        return databaseShardDirectoryPartMap(part) as Y.Map<unknown>;
      },
      createDatabaseShardManifest({
        workspaceId: doc.workspace.id,
        databaseId: snapshot.id,
        databaseRootDocId: reference.databaseRootDocId!,
        shards: entries,
      })
    );
    const columns = original.get('prop:columns');
    const copiedColumns = new Y.Array<unknown>();
    block.set('prop:columns', copiedColumns);
    copiedColumns.push(
      cloneDatabaseColumns(
        columns instanceof Y.AbstractType ? columns.toJSON() : columns,
        {
          sourceDatabaseId: source.databaseId,
          targetDatabaseId: snapshot.id,
          sourceHostDocId: source.docId,
          targetHostDocId: doc.id,
        }
      )
    );
    block.delete('prop:comments');
    const update = Y.encodeStateAsUpdate(targetRoot);
    if (update.length > DOCUMENT_PRODUCT_HARD_LIMIT_BYTES)
      throw new Error('Database root exceeds its byte budget');
    prepared.push({ docId: reference.databaseRootDocId!, update });
    for (const [partDocId, part] of targetParts)
      prepared.push({ docId: partDocId, update: Y.encodeStateAsUpdate(part) });
  } finally {
    targetRoot.destroy();
    targetParts.forEach(part => part.destroy());
  }
  // Root, parts and shards publish as one creation of at most 64 documents.
  if (prepared.length > 64) {
    throw new Error(
      'Database copy exceeds its shard budget or has unfinished shards'
    );
  }
  // Recheck the host before publishing anything; the clipboard cannot move a
  // database by claiming its identity or copy a deleted source from cache.
  await doc.pageStructure.loadMissingPartsOf([snapshot.id]);
  const sourceHost = doc.workspace.getDoc(source.docId)?.getStore();
  const sourceModel = sourceHost?.getModelById(source.blockId);
  const sourceProps = sourceModel?.props as Record<string, unknown> | undefined;
  if (
    sourceModel?.flavour !== 'affine:database' ||
    sourceProps?.databaseId !== source.databaseId ||
    !parentId ||
    doc.hasBlock(snapshot.id)
  ) {
    throw new Error('Database copy source or destination changed');
  }
  for (const item of prepared) {
    const target = doc.workspace.getDoc(item.docId);
    if (
      target &&
      (target.spaceDoc.getMap('blocks').size ||
        target.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY).size ||
        target.spaceDoc.getMap(DATABASE_DIRECTORY_PART_MAP_KEY).size ||
        target.spaceDoc.getArray(DATABASE_DIRECTORY_PART_SHARDS_ARRAY_KEY)
          .length)
    )
      throw new PublicationError('LOCAL_CONFLICT');
  }
  const parent = parentId;
  return async hold => {
    await publishDatabaseCreationDocuments(
      doc,
      prepared,
      livePageHosting(
        doc,
        () => {
          // The parent may have been pasted with this block.
          if (!doc.getModelById(parent) || doc.hasBlock(snapshot.id))
            throw new PublicationError('FORMAT_MISMATCH');
          hold(() =>
            doc.addBlock(
              'affine:database',
              { ...draft.props, ...reference, id: snapshot.id },
              parent,
              index
            )
          );
          return snapshot.id;
        },
        blockId =>
          hold(() => {
            const block = doc.getBlock(blockId)?.model;
            if (block) doc.deleteBlock(block);
          })
      )
    );
  };
}

function equal(a: Uint8Array, b: Uint8Array): boolean {
  return a.length === b.length && a.every((value, i) => value === b[i]);
}
